import { useLines } from "../config/LinesProvider";
import type { Journey } from "../contract/types";
import { journeyLabel } from "./JourneyPicker";
import type { ToolsScopeMode } from "./toolsScope";

/**
 * Étiquette du bouton « véhicule » quand un véhicule EST sélectionné —
 * volontairement générique (« Ce véhicule », pas « F5 · bus 3152 · sortie 09 »)
 * : la course choisie est déjà affichée en détail juste au-dessus (fiche
 * course) ; la répéter ici, tronquée, n'ajoutait rien. Le libellé complet
 * reste dans `title` (survol). Sans véhicule (bouton désactivé), l'étiquette
 * reste « Véhicule » — générique aussi, mais qui n'affirme pas « ce » véhicule
 * quand aucun n'est choisi.
 */
const VEHICLE_LABEL_SELECTED = "Ce véhicule";

/**
 * Commutateur de portée des outils : « Réseau entier » | « le véhicule
 * sélectionné ». Dit en clair, sous les boutons, ce sur quoi portent les outils
 * ci-dessous — c'est ce qui manquait quand les deux modes se mélangeaient.
 */
export function ScopeSwitch({
  mode,
  onMode,
  journey,
}: {
  mode: ToolsScopeMode;
  onMode: (mode: ToolsScopeMode) => void;
  /** Véhicule sélectionné, s'il y en a un (`null` : seul « Réseau entier » est possible). */
  journey: Journey | null;
}) {
  const lines = useLines();
  const vehicleTitle = journey ? journeyLabel(journey, lines.code(journey.lineId)) : undefined;

  return (
    <div style={{ padding: "8px 14px 10px", borderTop: "1px solid var(--sentinelle-rule)" }}>
      <div className="buttons has-addons is-fullwidth" role="group" aria-label="Portée des outils" style={{ marginBottom: 4 }}>
        <button
          type="button"
          className={`button is-small${mode === "network" ? " is-link is-selected" : ""}`}
          aria-pressed={mode === "network"}
          onClick={() => onMode("network")}
          style={{ flex: 1 }}
        >
          Réseau entier
        </button>
        <button
          type="button"
          className={`button is-small${mode === "vehicle" ? " is-link is-selected" : ""}`}
          aria-pressed={mode === "vehicle"}
          disabled={!journey}
          onClick={() => onMode("vehicle")}
          title={vehicleTitle ?? "Sélectionner un véhicule (carte, recherche ou Avance/Retard)"}
          style={{ flex: 2, minWidth: 0 }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {journey ? VEHICLE_LABEL_SELECTED : "Véhicule"}
          </span>
        </button>
      </div>
      <p className="has-text-grey" style={{ fontSize: "0.7rem", margin: 0 }} aria-live="polite">
        {mode === "vehicle"
          ? "Les outils ne portent que sur ce véhicule."
          : journey
            ? "Les outils portent sur tout le réseau (véhicule ouvert mis de côté)."
            : "Les outils portent sur tout le réseau. Sélectionner un véhicule pour les cibler."}
      </p>
    </div>
  );
}
